import { Button, Input, Modal, Row, Col, theme } from "antd";
import React, { useState, ComponentType } from "react";
import {
  PlusOutlined,
  FileMarkdownOutlined,
  CompassOutlined,
} from "@ant-design/icons";
import { Flex } from "../Flex";
import { Hover } from "../Hover";
import { VzblCard } from "../VzblCard";
import { TextTruncated } from "../TextTruncated";
import { CollapsibleTags } from "../CollapsibleTags";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { VizIcon } from "../VizIcon";
import { useQuestionFilter } from "src/hooks/useQuestionFilter";
import { useToasterContext, ToasterMessageType } from "src/hooks/useToaster";
import { AntdIconProps } from "@ant-design/icons/lib/components/AntdIcon";

export interface CardOption {
  key: string;
  name: string;
  label: string | JSX.Element;
  value: string | JSX.Element;
  disabled?: boolean;
  state: {
    params: any;
    viz: {
      value: any;
      config: any;
    };
  };
  icon: string | JSX.Element;
  tags?: string[];
}

export interface CardGroup {
  label: string;
  options: CardOption[];
}

export interface NewCardPickerProps {
  // Core props
  onCardSelect: (card: CardOption) => void;
  cardGroups: CardGroup[];

  // Optional features
  showMarkdownCard?: boolean;
  showBlankCard?: boolean;

  // UI customization
  triggerButton?: React.ReactNode;
  modalTitle?: string;
  modalWidth?: number;

  // Additional filters/features
  showEntityFilter?: boolean;
  showVisualizationFilter?: boolean;
  showTagsFilter?: boolean;

  // Custom renderers
  renderCard?: (card: CardOption) => React.ReactNode;
  renderTrigger?: (onClick: () => void) => React.ReactNode;
}

export const NewCardPicker: React.FC<NewCardPickerProps> = ({
  onCardSelect,
  cardGroups,
  showMarkdownCard = false,
  showBlankCard = false,
  modalTitle = "Add Card",
  modalWidth = 900,
  showEntityFilter = false,
  showVisualizationFilter = false,
  showTagsFilter = false,
  renderCard,
  renderTrigger,
}) => {
  const { token } = theme.useToken();
  const [modalOpen, setModalOpen] = useState(false);
  const toaster = useToasterContext();

  // Combine static cards with provided card groups
  const allCardGroups = React.useMemo(() => {
    const staticCards: CardOption[] = [];

    if (showMarkdownCard) {
      staticCards.push({
        key: "markdown-card",
        name: "-- markdown html text card",
        label: (
          <Flex start centeredY gap={8}>
            <div style={{ paddingTop: 2 }}>
              <FileMarkdownOutlined />
            </div>
            <span>Add Markdown Card</span>
          </Flex>
        ),
        value: "markdown",
        state: {
          params: {},
          viz: {
            value: null,
            config: {},
          },
        },
        icon: <FileMarkdownOutlined />,
      });
    }

    if (showBlankCard) {
      staticCards.push({
        key: "question-card",
        name: "-- explore question new empty card",
        label: (
          <Flex start centeredY gap={8}>
            <div style={{ paddingTop: 2 }}>
              <CompassOutlined />
            </div>
            <span>Add Blank Card</span>
          </Flex>
        ),
        value: "question",
        state: {
          params: {},
          viz: {
            value: null,
            config: {},
          },
        },
        icon: <CompassOutlined />,
      });
    }

    return staticCards.length > 0
      ? [{ label: "Create New", options: staticCards }, ...cardGroups]
      : cardGroups;
  }, [cardGroups, showMarkdownCard, showBlankCard]);

  const {
    filteredQuestions: filteredGroups,
    searchValue,
    toggleFilter,
    clearFilters,
    activeFilters,
    parseAndApplySearch,
  } = useQuestionFilter(allCardGroups);

  // Extract filter options from card groups
  const entityTypes = React.useMemo(() => {
    if (!showEntityFilter) return [];
    return [
      ...new Set(
        allCardGroups
          .flatMap((group) => group.options)
          .flatMap((option) => option.state?.params?.select)
          .filter(Boolean)
      ),
    ].sort();
  }, [allCardGroups, showEntityFilter]);

  const vizTypes = React.useMemo(() => {
    if (!showVisualizationFilter) return [];
    return [
      ...new Set(
        allCardGroups
          .flatMap((group) => group.options)
          .map((option) => option.state?.viz?.value)
          .filter(Boolean)
      ),
    ].sort();
  }, [allCardGroups, showVisualizationFilter]);

  const tags = React.useMemo(() => {
    if (!showTagsFilter) return [];
    return [
      ...new Set(
        allCardGroups
          .flatMap((group) => group.options)
          .flatMap((option) => option.tags || [])
          .filter(Boolean)
      ),
    ].sort();
  }, [allCardGroups, showTagsFilter]);

  const handleSelect = (card: CardOption) => {
    if (card.disabled) return;
    onCardSelect(card);
    clearFilters();
    setModalOpen(false);
  };

  const defaultTrigger = (
    <Hover
      baseStyle={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 60,
        height: 60,
        backgroundColor: token.colorBgContainer,
        borderRadius: "50%",
        color: token.colorPrimary,
        cursor: "pointer",
        border: `1px solid ${token.colorBorder}`,
        transition: "all 0.2s ease",
        boxShadow: token.boxShadowTertiary,
      }}
      hoverStyle={{
        backgroundColor: token.colorPrimaryBg,
        transform: "scale(1.05)",
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <div
        onClick={() => setModalOpen(true)}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PlusOutlined style={{ fontSize: 24 }} />
      </div>
    </Hover>
  );

  return (
    <>
      {renderTrigger ? renderTrigger(() => setModalOpen(true)) : defaultTrigger}

      {modalOpen && (
        <Modal
          open={true}
          onCancel={() => {
            clearFilters();
            setModalOpen(false);
          }}
          footer={null}
          title={modalTitle}
          width={modalWidth}
          style={{
            maxHeight: "80vh",
            top: "10vh",
          }}
          bodyStyle={{
            height: "calc(80vh - 110px)",
            overflow: "hidden",
            padding: "16px",
          }}
        >
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Input.Search
              value={searchValue}
              onChange={(e) => parseAndApplySearch(e.target.value)}
              placeholder="Filter cards..."
              style={{ marginBottom: 16 }}
              size="large"
            />
            <Row style={{ flex: 1, overflow: "hidden" }}>
              <Col
                span={8}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  paddingRight: 16,
                  borderRight: `1px solid ${token.colorBorder}`,
                }}
              >
                {showEntityFilter && entityTypes.length > 0 && (
                  <CollapsibleTags
                    title="Entity"
                    tags={entityTypes}
                    activeFilters={activeFilters}
                    attribute="select"
                    onTagClick={(tag) => toggleFilter(`select:${tag}`)}
                    colorPrimaryActive={token.colorPrimaryActive}
                  />
                )}

                {showTagsFilter && tags.length > 0 && (
                  <CollapsibleTags
                    title="Tags"
                    tags={tags}
                    activeFilters={activeFilters}
                    attribute="tag"
                    onTagClick={(tag) => toggleFilter(`tag:${tag}`)}
                    colorPrimaryActive={token.colorPrimaryActive}
                  />
                )}

                {showVisualizationFilter && vizTypes.length > 0 && (
                  <CollapsibleTags
                    title="Visualization"
                    tags={vizTypes}
                    activeFilters={activeFilters}
                    attribute="visualization"
                    onTagClick={(tag) => toggleFilter(`visualization:${tag}`)}
                    colorPrimaryActive={token.colorPrimaryActive}
                  />
                )}
              </Col>
              <Col
                span={16}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  paddingLeft: 16,
                }}
              >
                {filteredGroups.map((group) => (
                  <div style={{ marginBottom: 30 }} key={group.label}>
                    <h3>
                      {group.label} ({group.options?.length})
                    </h3>
                    <div>
                      <Flex
                        wrap
                        gap={16}
                        style={{
                          width: "100%",
                          justifyContent: "flex-start",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                        }}
                      >
                        {group.options?.map((card) =>
                          renderCard ? (
                            renderCard(card)
                          ) : (
                            <VzblCard
                              key={card.key}
                              title={
                                <TextTruncated lines={2} lineHeight={"1.2em"}>
                                  {card.label}
                                </TextTruncated>
                              }
                              icon={card.icon}
                              onClick={() => handleSelect(card)}
                              style={{
                                cursor: "pointer",
                                width: "calc(50% - 10px)",
                                opacity: card.disabled ? 0.5 : 1,
                              }}
                            />
                          )
                        )}
                      </Flex>
                    </div>
                  </div>
                ))}
              </Col>
            </Row>
          </div>
        </Modal>
      )}
    </>
  );
};
